import type React from 'react';
import { useEffect, useId, useRef, useState } from 'react';

interface SparklineProps {
  values: number[];
  height?: number;
  className?: string;
  /** Override the stroke/fill color. Defaults to shadcn chart-1 slate. */
  color?: string;
}

const DEFAULT_W = 100;
const PAD_Y = 2;
const DEFAULT_COLOR = 'hsl(222.2, 47.4%, 11.2%)';

/** Minimal SVG sparkline — no axes, no labels, no dots. Designed for embedding
 *  inside KPI tiles where the chart is decorative context, not the main signal. */
const Sparkline: React.FC<SparklineProps> = ({
  values,
  height = 32,
  className = '',
  color = DEFAULT_COLOR,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(DEFAULT_W);
  const gradientId = useId();

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setWidth(Math.max(40, Math.round(w)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const cleaned = values.filter((v) => Number.isFinite(v));

  if (cleaned.length < 2) {
    return (
      <div
        ref={containerRef}
        className={`w-full ${className}`}
        style={{ height }}
      />
    );
  }

  const min = Math.min(...cleaned);
  const max = Math.max(...cleaned);
  const range = max - min || 1;

  const points = cleaned.map((v, i) => {
    const x = (i / (cleaned.length - 1)) * width;
    const y = PAD_Y + (1 - (v - min) / range) * (height - 2 * PAD_Y);
    return { x, y };
  });

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(' ');

  const lastX = points[points.length - 1].x.toFixed(2);
  const firstX = points[0].x.toFixed(2);
  const areaPath = `${linePath} L ${lastX} ${height} L ${firstX} ${height} Z`;

  return (
    <div ref={containerRef} className={`w-full ${className}`}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        className="block w-full"
        role="img"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#${gradientId})`} stroke="none" />
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
};

export default Sparkline;
